import { useCallback, useRef } from "react";

/**
 * The `useIntersectionCallback` function creates an IntersectionObserver to trigger a callback when a
 * specified element intersects with the viewport.
 * @param {void} onIntersectCallback - The `onIntersectCallback` parameter is a function that will be called
 * when the observed element intersects with the viewport.
 * @returns {void} The `useIntersectionCallback` function returns a callback function that takes a `node`
 * parameter of type `HTMLDivElement | null`. This callback function sets up an `IntersectionObserver`
 * to observe the given `node` and calls the `onIntersectCallback` function when the observed element
 * intersects with the viewport.
 */
export function useIntersectionCallback(
  onIntersectCallback: () => void,
): (node: HTMLDivElement | null) => void {
  const intersectionObserverRef = useRef<IntersectionObserver | null>(null);

  return useCallback(
    (node: HTMLDivElement | null) => {
      if (intersectionObserverRef.current) {
        intersectionObserverRef.current.disconnect();
      }

      intersectionObserverRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          onIntersectCallback();
        }
      });

      if (node) {
        intersectionObserverRef.current.observe(node);
      }
    },
    [onIntersectCallback],
  );
}
